import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MdOutlineCancel } from "react-icons/md";

import ConfirmationModal from "../../../../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../../../../components/Form/Button";
import { StorageBox, StorageBoxDto } from "../../../../../../../../models/storage.box";
import { Description, DescriptionWrapper, Warning } from "./styles";

interface DeleteStorageBoxModalProps {
  onDelete: (storageBox?: StorageBoxDto) => void;
  isDeleting?: boolean;
  hasError?: boolean;
}

export interface DeleteStorageBoxHandle {
  onOpenModal: (data: StorageBox) => void;
  onCloseModal: () => void;
}

const ConfirmDeleteStorageBoxModal: ForwardRefRenderFunction<
  DeleteStorageBoxHandle,
  DeleteStorageBoxModalProps
> = (props, ref) => {
  const { onDelete, isDeleting, hasError } = props;
  const { t: translate } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [storageBox, setStorageBox] = useState<StorageBox>();

  const onOpenModal = (data: StorageBox) => {
    setStorageBox(data);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    onOpenModal,
    onCloseModal: () => setOpen(false),
  }));

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setOpen(false);
    }
  }, [hasError]);

  return (
    <ConfirmationModal
      open={open}
      closeModal={() => setOpen(false)}
      type="error"
      title={translate("deleteStorageBox")}
      leftIconTitle={<MdOutlineCancel color="#F04438" size={20} />}
      loading={isDeleting}
      cancelDisabled={isDeleting}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.ERROR}
      textCancel={translate("back")}
      textSubmit={translate("delete")}
      submit={() => onDelete({ id: storageBox?.id } as StorageBoxDto)}
      width="520px !important"
    >
      <Grid container spacing={2} className="mt-2">
        <Grid item className="w-100">
          <DescriptionWrapper data-cy="confirmationModalCancelBody">
            <Description>
              {translate("areYouSureYouWantToDeleteThisStorageBox")}{" "}
              {storageBox?.number ? storageBox?.number : ""}?
            </Description>

            <Warning className="error">
              {translate("thisActionIsPermanentAndCannotBeUndone")}
            </Warning>
          </DescriptionWrapper>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(ConfirmDeleteStorageBoxModal);
